import { useMemo, useState } from "react"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { AnalyzeSelectionDialog, type AnalysisItem } from "@/components/AnalyzeSelectionDialog"
import { generateTenderSummary } from "@/lib/api"
import type { Bid } from "@/lib/schemas"
import type { ExtractionCategory } from "@/types/extraction"
import { useBidsStore } from "@/stores/bids-store"
import { useDocumentsStore } from "@/stores/documents-store"
import { hasCompletedRun, isRanAndEmpty, useExtractionStore } from "@/stores/extraction-store"
import { useTenderOverviewStore } from "@/stores/tender-overview-store"
import { cn } from "@/lib/utils"

const ANALYZE_DISABLED_HINT_ID = "analyze-bid-disabled-reason"

const EXTRACTION_CATEGORIES: readonly ExtractionCategory[] = [
  "evaluation_criteria",
  "compliance_matrix",
  "submission_checklist",
  "key_dates",
  "risk_register",
]

const CATEGORY_META: Record<ExtractionCategory, { label: string; description: string }> = {
  evaluation_criteria: { label: "Evaluation Criteria", description: "Extract weighting and scoring structure" },
  compliance_matrix: { label: "Compliance Matrix", description: "Identify mandatory compliance requirements" },
  submission_checklist: { label: "Submission Checklist", description: "List required submission documents and formats" },
  key_dates: { label: "Key Dates", description: "Extract important milestones and deadlines" },
  risk_register: { label: "Risk Register", description: "Identify contractual and commercial risks" },
}

/**
 * True once any analysis has produced a result for the bid. Overview offers
 * "Analyse bid" only before this; afterwards the button lives on Qualify.
 * A run in flight does not count, so the button doesn't vanish mid-click.
 */
export function useHasCompletedAnalysis(): boolean {
  const categories = useExtractionStore((s) => s.categories)
  const tenderOverview = useTenderOverviewStore((s) => s.overview)
  return (
    tenderOverview !== null ||
    EXTRACTION_CATEGORIES.some(
      (c) => hasCompletedRun(categories[c]) || categories[c].state === "populated"
    )
  )
}

interface AnalyseBidButtonProps {
  bid: Bid
  readOnly: boolean
}

/** "Analyse bid": picks what is still to analyse and starts it. */
export function AnalyseBidButton({ bid, readOnly }: AnalyseBidButtonProps) {
  const bidId = bid.id
  const fetchBidById = useBidsStore((s) => s.fetchBidById)
  const documents = useDocumentsStore((s) => s.documents)
  const hasProcessedDocuments = useDocumentsStore((s) => s.hasProcessedDocuments)
  const categories = useExtractionStore((state) => state.categories)
  const startExtraction = useExtractionStore((state) => state.startExtraction)
  const tenderOverview = useTenderOverviewStore((state) => state.overview)
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false)

  const totalCount = documents.length
  const hasProcessed = hasProcessedDocuments()

  // Categories that have never produced a completed run. A category that ran and
  // returned zero items is DONE — listing it here would re-arm the Analyse button
  // and (because the dialog pre-selects everything) re-bill the user for analysis
  // that already completed.
  const neverRunCategories = EXTRACTION_CATEGORIES.filter(
    (category) => !isRanAndEmpty(categories[category]) && categories[category].state === "empty"
  )
  // Any extraction work in flight — a live stream OR the post-drop recovery poll.
  // A recovering category has `state: 'empty'` with `isRecovering: true`: the
  // stream died but the Lambda is still running and the store is polling for the
  // result. Reading only `state` left it listed and pre-selected with the button
  // enabled, so confirming fired a SECOND run for work the backend was still
  // finishing — two charges, one result.
  const isExtracting = EXTRACTION_CATEGORIES.some(
    (category) => categories[category].state === "extracting" || categories[category].isRecovering
  )
  const needsTenderSummary = !bid.tenderSummary?.trim()
  const needsTenderOverview = tenderOverview === null
  const hasAnalyzeWork = neverRunCategories.length > 0 || needsTenderSummary || needsTenderOverview
  const canRunAnalysis = hasProcessed && !isExtracting && !isGeneratingSummary && !readOnly && hasAnalyzeWork
  const isDisabled = !canRunAnalysis || totalCount === 0

  // Why the action is unavailable, as visible text linked by aria-describedby.
  // A `title` never fires: the Button base sets `disabled:pointer-events-none`.
  const analyzeDisabledReason =
    totalCount === 0 ? "Upload documents first" : !hasProcessed ? "Documents still processing" : null

  const analysisItems = useMemo(() => {
    const items: AnalysisItem[] = []
    if (needsTenderSummary) {
      items.push({
        key: "tender_summary",
        label: "Overview snapshot",
        description: "A 3–4 sentence summary for the Overview card",
        mandatory: true,
      })
    }
    if (needsTenderOverview) {
      items.push({
        key: "tender_overview",
        label: "Tender Summary",
        description: "Detailed analysis of scope, contract, client and risks",
        mandatory: false,
      })
    }
    for (const category of neverRunCategories) {
      const meta = CATEGORY_META[category]
      items.push({ key: category, label: meta.label, description: meta.description, mandatory: false })
    }
    return items
  }, [needsTenderSummary, needsTenderOverview, neverRunCategories])

  const handleAnalyzeBid = async (selectedKeys: AnalysisItem["key"][]) => {
    if (readOnly) return

    // Both non-extraction keys (the Overview snapshot and the detailed Tender
    // Summary) have their own dispatch paths below.
    const selectedCategories = selectedKeys.filter(
      (key): key is ExtractionCategory => key !== "tender_summary" && key !== "tender_overview"
    )
    selectedCategories.forEach((category) => void startExtraction(bidId, category))

    if (selectedKeys.includes("tender_overview")) {
      void useTenderOverviewStore.getState().generate(bidId)
    }

    if (selectedKeys.includes("tender_summary") && needsTenderSummary) {
      setIsGeneratingSummary(true)
      try {
        await generateTenderSummary(bidId)
        await fetchBidById(bidId)
      } catch {
        // Error surfaced by API layer
      } finally {
        setIsGeneratingSummary(false)
      }
    }
  }

  return (
    <>
      {analyzeDisabledReason && (
        <span id={ANALYZE_DISABLED_HINT_ID} className="text-meta">
          {analyzeDisabledReason}
        </span>
      )}
      <AnalyzeSelectionDialog
        trigger={
          <Button
            disabled={isDisabled}
            size="sm"
            className={cn("gap-2", isDisabled && "opacity-50")}
            aria-describedby={analyzeDisabledReason ? ANALYZE_DISABLED_HINT_ID : undefined}
          >
            {isExtracting || isGeneratingSummary ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analysing...
              </>
            ) : (
              <>
                <GradientSparkleIcon className="h-4 w-4" />
                {hasAnalyzeWork ? "Analyse bid" : "Analysis complete"}
              </>
            )}
          </Button>
        }
        items={analysisItems}
        onConfirm={(keys) => void handleAnalyzeBid(keys)}
        disabled={isDisabled}
      />
    </>
  )
}
